import { resolveSegmentDecision } from '../../../../shared/vacations/segmentDecisions';
import { format } from 'date-fns';
import { normalizeString } from '@/utils/stringUtils';
import { createDateSafe } from '@/utils/dateUtils';
import { countVacationDays } from '@/features/Collaborators/Edit/Form/Fieldsets/VacationFieldset/vacationPeriodTotals';
import { buildRequestMilestones } from './vacationHistoryMilestones';
import { getRequestStatusMetadata } from '../vacations.definitions';
import type { VacationRequest } from '../models/vacation.types';
import type { Collaborator, VacationPeriod } from '@/features/Collaborators/Models/collaborator.types';
import type {
    HistoryRequestRow,
    HistoryEventKind,
    HistoryReportCounts,
} from '../models/vacationHistoryReport.types';
import type { CollaboratorMaps } from '../hooks/data/useVacationHistoryDataQuery';

export function buildHistoryRequestRows(
    requests: VacationRequest[],
    collaborators: Collaborator[],
    collaboratorMaps: CollaboratorMaps,
    today: Date = new Date()
): HistoryRequestRow[] {
    const rows: HistoryRequestRow[] = [];

    requests.forEach((request) => {
        const validSegments = request.segments.filter((seg) => Boolean(seg.startDate) && Boolean(seg.endDate));

        if (validSegments.length === 0) return;

        const milestones = buildRequestMilestones(request, { byEmail: collaboratorMaps.byEmail }, today);
        const statusMeta = getRequestStatusMetadata(request);

        rows.push({
            requestId: request.id,
            collaborator: {
                id: request.collaboratorId,
                name: request.collaboratorName,
                email: request.collaboratorEmail,
            },
            segments: validSegments,
            segmentDecisions: Object.fromEntries(
                validSegments
                    .map((s) => [String(s.order), resolveSegmentDecision(request, s.order)] as const)
                    .filter((entry): entry is [string, NonNullable<typeof entry[1]>] => Boolean(entry[1]))
            ),
            totalDays: validSegments.reduce((sum, s) => sum + (s.days || 0), 0),
            hasApprovedSell: request.sellRequest.status === 'Approved',
            sellDays: request.sellRequest.status === 'Approved' ? request.sellRequest.days || 0 : 0,
            currentStatus: statusMeta.label,
            currentStatusClass: statusMeta.className,
            milestones,
            isManual: false,
            acquisitionPeriodId: request.acquisitionPeriodId || undefined,
            concessiveDeadline: request.concessiveDeadline || undefined,
            requestStatus: request.status,
            sellRequestStatus: (request.sellRequest.status as any),
        });
    });

    collaborators.forEach((collaborator) => {
        if (!collaborator.vacationPeriods || collaborator.vacationPeriods.length === 0) return;

        collaborator.vacationPeriods.forEach((period, index) => {
            const todayDate = new Date(today);
            todayDate.setHours(0, 0, 0, 0);
            const periodStart = createDateSafe(period.startDate);
            const manualStatus = periodStart && periodStart <= todayDate ? 'Completed' : 'Scheduled';

            rows.push({
                requestId: null,
                collaborator: {
                    id: collaborator.id,
                    name: collaborator.name,
                    email: collaborator.email || '',
                },
                segments: [
                    {
                        order: 1,
                        startDate: period.startDate,
                        endDate: period.endDate,
                        days: countVacationDays(period.startDate, period.endDate),
                    },
                ],
                segmentDecisions: undefined,
                totalDays: countVacationDays(period.startDate, period.endDate),
                hasApprovedSell: false,
                sellDays: 0,
                currentStatus: 'Lançamento manual',
                currentStatusClass: 'bg-gray-100 text-gray-700',
                milestones: [],
                isManual: true,
                acquisitionPeriodId: undefined,
                concessiveDeadline: undefined,
                observations: period.observations,
                manualIndex: index,
                requestStatus: manualStatus as any,
                sellRequestStatus: undefined,
            });
        });
    });

    return rows;
}

export function filterHistoryRows(
    rows: HistoryRequestRow[],
    options: {
        kind: HistoryEventKind;
        periodKey: string;
        search: string;
    }
): HistoryRequestRow[] {
    let filtered = rows;

    if (options.search.trim()) {
        const term = normalizeString(options.search);
        filtered = filtered.filter((row) =>
            normalizeString(`${row.collaborator.name} ${row.collaborator.email}`).includes(term)
        );
    }

    filtered = filtered.filter((row) => {
        if (row.isManual) {
            if (!['requested', 'started', 'completed'].includes(options.kind)) {
                return false;
            }

            const startDate = row.segments[0]?.startDate;
            if (!startDate) return false;

            if (options.kind === 'requested') return false;
            if (options.kind === 'started') {
                if (!options.periodKey) return true;
                return startDate.startsWith(options.periodKey);
            }
            if (options.kind === 'completed') {
                const endDate = row.segments[0]?.endDate;
                if (!endDate || !options.periodKey) return !!endDate;
                return endDate.startsWith(options.periodKey);
            }
            return false;
        }

        if (!row.milestones.some((m) => m.kind === options.kind)) {
            return false;
        }

        if (!options.periodKey) return true;

        return row.milestones.some((m) => {
            if (m.kind !== options.kind || !m.occurredAt) return false;
            const monthKey = format(m.occurredAt, 'yyyy-MM');
            return monthKey.startsWith(options.periodKey);
        });
    });

    return filtered.sort((a, b) => {
        const aDate = getFirstMilestoneDate(a);
        const bDate = getFirstMilestoneDate(b);

        if (aDate && bDate) {
            const diff = aDate.getTime() - bDate.getTime();
            if (diff !== 0) return diff;
        }

        return a.collaborator.name.localeCompare(b.collaborator.name, 'pt-BR');
    });
}

function getFirstMilestoneDate(row: HistoryRequestRow): Date | null {
    if (row.isManual) {
        const startDate = row.segments[0]?.startDate;
        if (startDate) {
            return new Date(startDate);
        }
        return null;
    }

    const milestone = row.milestones[0];
    return milestone?.occurredAt ?? null;
}

export function buildHistoryYearOptions(rows: HistoryRequestRow[], today: Date = new Date()): number[] {
    const years = new Set<number>([today.getFullYear()]);

    rows.forEach((row) => {
        if (row.isManual) {
            const year = parseInt(row.segments[0]?.startDate.substring(0, 4) ?? String(today.getFullYear()), 10);
            years.add(year);
        } else {
            row.milestones.forEach((m) => {
                if (m.occurredAt) {
                    years.add(m.occurredAt.getFullYear());
                }
            });
        }
    });

    return Array.from(years).sort((a, b) => a - b);
}

export function selectVisibleSegments(
    row: HistoryRequestRow,
    kind: HistoryEventKind,
    periodKey: string | null
): Array<{ segment: typeof row.segments[0]; order: number }> {
    const allSegments = row.segments.map((seg, idx) => {
        const order = seg.order || idx + 1;
        const decision = row.segmentDecisions?.[String(order)];
        const isRejected = decision?.status === 'Rejected';

        if (isRejected) return null;

        const startDate = decision?.startDate || seg.startDate;
        const endDate = decision?.endDate || seg.endDate;

        if (!startDate || !endDate) return null;

        return {
            segment: { ...seg, startDate, endDate },
            order,
            startDate,
            endDate,
            isRejected,
        };
    }).filter(Boolean) as Array<any>;

    if (!periodKey || (kind !== 'started' && kind !== 'completed')) {
        return allSegments.map((item) => ({ segment: item.segment, order: item.order }));
    }

    return allSegments
        .filter((item) => {
            const dateToCheck = kind === 'started' ? item.startDate : item.endDate;
            return dateToCheck.startsWith(periodKey);
        })
        .map((item) => ({ segment: item.segment, order: item.order }));
}

export function countHistoryRows(rows: HistoryRequestRow[]): HistoryReportCounts {
    const people = new Set(rows.map((r) => r.collaborator.id)).size;
    return { total: rows.length, people };
}

export function buildHistoryCsvRows(
    rows: HistoryRequestRow[],
    options: { kind?: HistoryEventKind; periodKey?: string | null } = {}
): { headers: string[]; rows: string[][] } {
    const kind = options.kind ?? 'requested';
    const periodKey = options.periodKey ?? null;
    const headers = [
        'Colaborador',
        'E-mail',
        'Parcela',
        'Início',
        'Fim',
        'Dias',
        'Abono',
        'Situação atual',
        'Pedido em',
        'Gestor',
        'Aprovado gestor em',
        'Validado DP em',
        'Validado por',
    ];

    const csvRows = rows.flatMap((row) => {
        const requestedMilestone = row.milestones.find((m) => m.kind === 'requested');
        const managerMilestone = row.milestones.find((m) => m.kind === 'managerApproved');
        const dpMilestone = row.milestones.find((m) => m.kind === 'dpApproved');

        const visibleSegments = selectVisibleSegments(row, kind, periodKey).map(({ segment }) => segment);
        const segments = visibleSegments.length > 0 ? visibleSegments : [null];

        return segments.map((segment) => [
            row.collaborator.name,
            row.collaborator.email,
            segment ? `${segment.startDate} a ${segment.endDate}` : '',
            segment?.startDate ?? '',
            segment?.endDate ?? '',
            String(segment ? segment.days : row.totalDays),
            row.hasApprovedSell ? 'Sim' : 'Não',
            row.currentStatus,
            requestedMilestone?.occurredAt ? format(requestedMilestone.occurredAt, 'dd/MM/yyyy') : '',
            managerMilestone?.actorName ?? managerMilestone?.actorEmail ?? '',
            managerMilestone?.occurredAt ? format(managerMilestone.occurredAt, 'dd/MM/yyyy') : '',
            dpMilestone?.occurredAt ? format(dpMilestone.occurredAt, 'dd/MM/yyyy') : '',
            dpMilestone?.actorName ?? dpMilestone?.actorEmail ?? '',
        ]);
    });

    return { headers, rows: csvRows };
}
